import { isUserNotFoundError } from './auth-errors'

/**
 * Resolves a signup email address to its Cognito username via the
 * cognito-service `/auth/resolve-username` endpoint.
 *
 * The external pool uses `email` as a sign-in *alias*, which Cognito only
 * resolves *after* the email is verified. During `confirmSignUp` /
 * `resendSignUpCode` — which run *before* verification — the user is resolvable
 * only by their real username, so passing the email fails with Cognito's
 * cryptic "Username/client id combination not found". This unauthenticated
 * lookup maps the email back to the username first. See #2120.
 *
 * @param email the email address the user typed
 * @param serviceUrl base URL of the cognito-service API (from app config)
 * @returns the resolved Cognito username
 * @throws a user-friendly Error if the email can't be resolved or the service
 *   URL is missing
 */
export async function resolveUsername(
  email: string,
  serviceUrl: string | undefined,
): Promise<string> {
  if (!serviceUrl) {
    throw new Error(
      'Unable to look up your account right now. Please try entering your username instead.',
    )
  }

  let response: Response
  try {
    response = await fetch(`${serviceUrl}/auth/resolve-username`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
  } catch {
    throw new Error(
      'Unable to look up your account right now. Please try again, or enter your username instead.',
    )
  }

  if (response.status === 404) {
    throw new Error(
      "We couldn't find an unconfirmed account for that email address. Check the address, or enter your username instead.",
    )
  }

  if (!response.ok) {
    throw new Error(
      'Unable to look up your account right now. Please try again, or enter your username instead.',
    )
  }

  let payload: { username?: string }
  try {
    payload = (await response.json()) as { username?: string }
  } catch {
    // A 200 with an empty / non-JSON body is a broken response, not a
    // "no account" signal — surface the generic retry message.
    throw new Error(
      'Unable to look up your account right now. Please try again, or enter your username instead.',
    )
  }

  if (!payload.username) {
    throw new Error(
      "We couldn't find an unconfirmed account for that email address. Check the address, or enter your username instead.",
    )
  }

  return payload.username
}

/**
 * Best-effort "did this sign-in fail because the account is unconfirmed?" probe.
 *
 * Sign-in accepts a username OR an email. For an *unconfirmed* user the email
 * alias doesn't resolve yet (`email_verified=false`), so Cognito answers
 * `UserNotFoundException` — indistinguishable at the client from a genuinely
 * unknown address, which is why sign-in never reached the `CONFIRM_SIGN_UP`
 * redirect for exactly the users it exists to rescue. A resolved username here
 * means an `UNCONFIRMED` account exists (the endpoint is scoped to those), so
 * the caller can route to `/confirm-signup` with the *real* username. See #2407.
 *
 * Deliberately narrow and quiet: the lookup fires only on `UserNotFoundException`
 * with an email-shaped identifier — never on a wrong password — and any failure
 * (404, network, missing service URL) returns `undefined` so the caller falls
 * back to the original Cognito error.
 *
 * @param err the error Cognito's `signIn` threw
 * @param identifier the username or email the user typed
 * @param serviceUrl base URL of the cognito-service API (from app config)
 * @returns the resolved username, or `undefined` if this isn't the unconfirmed
 *   -email case or the lookup didn't succeed
 */
export async function tryResolveUnconfirmedUsername(
  err: unknown,
  identifier: string,
  serviceUrl: string | undefined,
): Promise<string | undefined> {
  // Guessing email-vs-username from the string alone is unsound (usernames can
  // contain '@'), so this only ever runs after Cognito has already failed to
  // resolve the identifier as-is.
  if (!isUserNotFoundError(err) || !identifier.includes('@')) {
    return undefined
  }

  try {
    return await resolveUsername(identifier, serviceUrl)
  } catch {
    return undefined
  }
}
